import { functionDoc } from '../functionDoc';
import { ROLES, SHOTS } from '../docHelpers';
import { grillaCampo, GRILLA_DETALLE_PREREQ, GRILLA_DETALLE_PROCEDURE } from './grillaField';

const detalleBase = (p) =>
  functionDoc({
    type: 'config',
    prerequisites: [...GRILLA_DETALLE_PREREQ, ...(p.extraPrerequisites || [])],
    procedure: GRILLA_DETALLE_PROCEDURE,
    practicalExample: p.practicalExample,
    validations: p.validations,
    expectedResult: p.expectedResult || [
      'Cada fila muestra los mismos controles documentados aquí.',
      'Al guardar, el backend valida producto, cantidad y campos obligatorios de esta variante.',
    ],
    commonErrors: p.commonErrors || [],
    relatedFunctions: p.relatedFunctions,
    observations: p.observations || [],
    screenshots: [SHOTS.formularioCompleto],
    roles: p.roles || 'Según módulo (Compras, Ventas o Almacenes).',
    ...p,
  });

/** Controles que aparecen encima de varias grillas (no son columnas de fila). */
export const grillaControlesComunesDoc = detalleBase({
  id: 'general-grilla-detalle-controles-comunes',
  name: 'Grilla — controles sobre la tabla (comunes)',
  route: '/manual',
  locationPath: 'Encima de la grilla en OC, guía de ingreso, venta con productos, etc.',
  generalDescription:
    'Controles que no son una columna de la fila pero afectan cómo se interpretan precios o valores por defecto de las líneas nuevas. Aparecen solo si el módulo y la configuración lo permiten.',
  interfaceElements: [
    grillaCampo(
      'Proyecto por defecto',
      'Select',
      'Lista de proyectos activos (código — nombre).',
      'Valor que heredan las líneas nuevas de producto. Visible en ProductLineDefaultsBar cuando el módulo de proyecto en productos está activo.',
      { required: false },
    ),
    grillaCampo(
      'IGV por defecto',
      'Select (Sí / No)',
      'Indica si las líneas nuevas se crean gravadas.',
      'Visible cuando IGV por línea está habilitado en configuración general.',
    ),
    grillaCampo(
      'Tipo de venta por defecto',
      'Select',
      'Tipos de venta del punto de venta.',
      'Solo en Ventas (captura rápida / defaults). Opción «(predeterminado)» marca el tipo por defecto del catálogo.',
    ),
    grillaCampo(
      'Los precios incluyen IGV',
      'Toggle / interruptor',
      'Define si el monto tipeado en precio unitario ya trae IGV.',
      'Presente en orden de compra, orden de servicio de compra y guía de ingreso valorizada. No aplica en Ventas (allí el precio de línea es siempre sin IGV en pantalla).',
      { required: false },
    ),
    grillaCampo(
      'Subtotal con: Medida convertida / Medida ingresada',
      'Dos botones',
      'Elige qué cantidad multiplica el precio cuando hay conversión de unidad personalizada.',
      'Solo si conversiones de producto están activas y alguna línea usa tasa distinta a la unidad base.',
    ),
    grillaCampo(
      'Agregar (captura rápida)',
      'Botón',
      'Confirma la fila temporal de la barra verde «Captura rápida».',
      'Valida producto y cantidad antes de añadir al listado inferior.',
      { required: true },
    ),
  ],
  relatedFunctions: ['general-grilla-productos-items'],
  observations: [
    'En requerimiento de compras no aparecen precios ni toggle de IGV.',
    'En OSV de venta no hay captura rápida de productos.',
  ],
});

export const grillaDetalleRequerimientoDoc = detalleBase({
  id: 'general-grilla-detalle-requerimiento-compras',
  name: 'Grilla — fila Requerimiento (Compras)',
  route: '/requirements/create',
  locationPath: 'Compras > Requerimientos > Nuevo/Editar > sección «Productos»',
  generalDescription:
    'Cada fila del requerimiento interno: solo cantidades y unidad, sin precio ni IGV. Misma estructura en captura rápida y en filas ya agregadas.',
  extraPrerequisites: ['Módulo Compras con permiso de escritura.'],
  interfaceElements: [
    grillaCampo(
      'Producto',
      'Buscador (ProductSelector)',
      'Catálogo global de productos por SKU o nombre.',
      'Obligatorio. Al elegir producto sugiere unidad de stock del maestro.',
      { required: true, example: 'SKU-1001' },
    ),
    grillaCampo(
      'Proyecto',
      'Select',
      'Proyecto o centro de costo.',
      'Opción vacía «—». Visible si productsProjectEnabled está activo (barra de defaults y columna).',
    ),
    grillaCampo(
      'Cant.',
      'Número decimal (input)',
      'Cantidad solicitada.',
      'Mínimo 0,001. Bajo el campo puede mostrarse QuantityConversionHint si hay conversión.',
      { required: true },
    ),
    grillaCampo(
      'Unidad',
      'Select (UnitSelect)',
      'Unidad de medida de la línea.',
      'Lista filtrada según producto; permite unidades equivalentes configuradas.',
    ),
    grillaCampo(
      'Quitar línea',
      'Botón (icono papelera)',
      'Elimina la fila.',
      'Solo si hay más de una línea y no está en solo lectura.',
    ),
    grillaCampo(
      '+ Agregar línea',
      'Botón',
      'Añade fila vacía.',
      'Oculto si captura rápida está activa (usa barra verde).',
    ),
    grillaCampo(
      'Descargar plantilla / Importar Excel',
      'Botón + archivo',
      'Carga masiva de SKU y cantidades.',
      'Abre ComprasItemsImportPreviewModal antes de aplicar.',
    ),
  ],
  relatedFunctions: [
    'general-grilla-productos-items',
    'compras-requerimientos-create',
    'general-grilla-detalle-oc-compras',
  ],
  roles: ROLES.limitado,
});

export const grillaDetalleOcComprasDoc = detalleBase({
  id: 'general-grilla-detalle-oc-compras',
  name: 'Grilla — fila Orden de compra (Compras)',
  route: '/purchase-orders/create',
  locationPath: 'Compras > Órdenes de compra > Nueva/Editar > «Items de la Orden»',
  generalDescription:
    'Fila de producto en orden de compra: incluye precio, IGV por línea y subtotal. Encima: toggle precios con IGV, modo de cantidad convertida y defaults de proyecto/IGV.',
  extraPrerequisites: ['Proveedor seleccionado en cabecera.', 'Tasa IGV en cabecera (p. ej. 18 %).'],
  interfaceElements: [
    grillaCampo(
      'Producto',
      'Buscador (ProductSelector)',
      'Producto a comprar.',
      'Obligatorio. Puede venir de importación Excel o de «Anidar requerimiento».',
      { required: true },
    ),
    grillaCampo(
      'Unidad',
      'Select (UnitSelect)',
      'Unidad de la línea.',
      'Editable por fila; afecta conversión y subtotal.',
    ),
    grillaCampo(
      'Proyecto',
      'Select',
      'Proyecto imputable.',
      'Opción «Seleccionar» vacía permitida.',
    ),
    grillaCampo(
      'Cant.',
      'Número (QuantityTextInput)',
      'Cantidad ordenada.',
      'Mínimo 0,001; hint de conversión bajo el campo si aplica.',
      { required: true },
    ),
    grillaCampo(
      'Precio Unit.',
      'Moneda (UnitPriceTextInput)',
      'Precio por unidad; prefijo S/ en pantalla.',
      'Interpretación con/sin IGV según toggle «Los precios incluyen IGV». PriceIgvHint bajo el campo.',
      { required: true },
    ),
    grillaCampo(
      'IGV',
      'Select (Sí / No)',
      'Línea gravada o no.',
      'Opciones «Sí» y «No»; interactúa con toggle de cabecera al calcular base.',
    ),
    grillaCampo(
      'Subtotal',
      'Solo lectura',
      'Importe de línea en soles.',
      'Puede mostrar hint «con IGV» si la línea está gravada.',
      { editable: false, automatic: true },
    ),
    grillaCampo(
      'Eliminar item',
      'Botón',
      'Quita la fila.',
      'Icono papelera en columna de acciones.',
    ),
    grillaCampo(
      'Anidar requerimiento',
      'Botón (cabecera grilla)',
      'Abre modal para importar líneas de REQ.',
      'No es columna; agrega filas al listado.',
    ),
  ],
  relatedFunctions: [
    'general-grilla-detalle-controles-comunes',
    'general-grilla-detalle-requerimiento-compras',
    'compras-oc-form',
  ],
  roles: ROLES.limitado,
});

export const grillaDetalleOsComprasDoc = detalleBase({
  id: 'general-grilla-detalle-os-compras',
  name: 'Grilla — fila Orden de servicio (Compras)',
  route: '/service-orders/create',
  locationPath: 'Compras > Órdenes de servicio > Nueva/Editar > «Items de la Orden»',
  generalDescription:
    'Solo servicios (no productos): cada fila es un servicio del catálogo de compras con descripción, proyecto, cantidad, precio e IGV. Sin captura rápida ni Excel de productos.',
  interfaceElements: [
    grillaCampo(
      'Servicio',
      'Buscador (ServiceSelector)',
      'Servicio del catálogo de compras.',
      'Obligatorio.',
      { required: true },
    ),
    grillaCampo(
      'Descripción',
      'Texto',
      'Detalle del servicio en el documento.',
      'Placeholder «Descripción»; recomendable completar para impresión.',
    ),
    grillaCampo(
      'Proyecto',
      'Select',
      'Proyecto.',
      'Opción «Seleccionar» vacía.',
    ),
    grillaCampo(
      'Cant.',
      'Número (QuantityTextInput)',
      'Cantidad del servicio.',
      'Obligatorio, mínimo 0,001.',
      { required: true },
    ),
    grillaCampo(
      'P.Unit.',
      'Moneda (UnitPriceTextInput)',
      'Precio unitario con prefijo S/.',
      'Sujeto a toggle «Los precios incluyen IGV» sobre la grilla.',
      { required: true },
    ),
    grillaCampo(
      'IGV',
      'Select (Sí / No)',
      'Gravado o exonerado.',
      'Valores true/false en select.',
    ),
    grillaCampo(
      'Subtotal',
      'Solo lectura',
      'Cantidad × precio neto de línea.',
      'No editable.',
      { editable: false, automatic: true },
    ),
    grillaCampo(
      'Eliminar item',
      'Botón',
      'Elimina la fila.',
      'Columna final.',
    ),
    grillaCampo(
      '+ Agregar Item',
      'Botón',
      'Nueva fila de servicio.',
      'Única forma de agregar (no hay captura rápida).',
    ),
  ],
  relatedFunctions: ['general-grilla-detalle-controles-comunes', 'compras-os-form'],
  observations: ['No confundir con OSV de Ventas (VentasCommercialForm con servicios de venta).'],
  roles: ROLES.limitado,
});

export const grillaDetalleVentasProductoDoc = detalleBase({
  id: 'general-grilla-detalle-ventas-producto',
  name: 'Grilla — fila Producto (Ventas)',
  route: '/ventas/orders/create',
  locationPath:
    'Ventas > Cotización / Registrar venta → bloque «Ítems», fila tipo producto',
  generalDescription:
    'Línea de producto en cotización o venta (y captura rápida equivalente). Precio siempre ingresado y mostrado sin IGV en pantalla (VENTAS_PRICES_INCLUDE_IGV).',
  extraPrerequisites: ['Punto de venta seleccionado.', 'Lista de precios del POS.'],
  interfaceElements: [
    grillaCampo(
      'Producto',
      'Buscador (VentasCatalogSelector)',
      'Producto u oferta del catálogo del POS.',
      'Ofertas pueden generar varias filas vinculadas; el valor puede ser id de oferta.',
      { required: true },
    ),
    grillaCampo(
      'Tipo de venta',
      'Select',
      'Clasificación comercial.',
      'Obligatorio si hay tipos configurados; oculto en líneas de oferta multi-producto.',
      { required: true },
    ),
    grillaCampo(
      'Proyecto',
      'Select',
      'Proyecto por línea.',
      'Opción «Seleccionar» vacía; default desde POS o barra de defaults.',
    ),
    grillaCampo(
      'Cant.',
      'Número (QuantityTextInput)',
      'Cantidad vendida o cotizada.',
      'QuantityConversionHint bajo el campo.',
      { required: true },
    ),
    grillaCampo(
      'P. unit.',
      'Moneda (UnitPriceTextInput)',
      'Precio unitario neto; prefijo S/.',
      'PriceIgvHint muestra equivalente con IGV de cabecera.',
      { required: true },
    ),
    grillaCampo(
      'IGV',
      'Select (Sí / No)',
      'Gravado por línea.',
      'Si productsIgvEnabled está apagado, puede ocultarse y usarse regla global.',
    ),
    grillaCampo(
      'Subtotal',
      'Solo lectura',
      'Importe de línea.',
      'Hint puede mostrar monto con IGV (alwaysConIgv en hint).',
      { editable: false, automatic: true },
    ),
    grillaCampo(
      'Quitar ítem (✕)',
      'Botón',
      'Elimina fila.',
      'Columna acciones.',
    ),
    grillaCampo(
      '+ Producto / + Servicio / Importar Excel',
      'Botones de cabecera',
      'Alta manual o masiva.',
      'OSV de venta no muestra + Producto ni Excel.',
    ),
  ],
  relatedFunctions: [
    'general-grilla-detalle-controles-comunes',
    'ventas-grilla-productos-items',
    'general-grilla-detalle-ventas-servicio',
  ],
  roles: ROLES.ventas,
});

export const grillaDetalleVentasServicioDoc = detalleBase({
  id: 'general-grilla-detalle-ventas-servicio',
  name: 'Grilla — fila Servicio (Ventas)',
  route: '/ventas/service-orders/create',
  locationPath:
    'Ventas > Cotización / OSV / Registrar venta → fila con item_type servicio',
  generalDescription:
    'Línea de servicio: catálogo de ventas + descripción obligatoria para PDF. Mismas columnas de cantidad, precio neto, IGV y subtotal que producto, sin selector de unidad separado (unidad del servicio en hint).',
  interfaceElements: [
    grillaCampo(
      'Servicio',
      'Buscador (ServiceSelector)',
      'Servicio del catálogo de ventas.',
      'Obligatorio.',
      { required: true },
    ),
    grillaCampo(
      'Descr.',
      'Texto',
      'Descripción impresa en PDF/comprobante.',
      'Obligatorio al validar; placeholder «Descripción del servicio».',
      { required: true },
    ),
    grillaCampo(
      'Tipo de venta',
      'Select',
      'Si hay tipos de venta en el POS.',
      'Igual que en fila producto.',
    ),
    grillaCampo(
      'Proyecto',
      'Select',
      'Proyecto de la línea.',
      'Opcional.',
    ),
    grillaCampo(
      'Cant.',
      'Número (QuantityTextInput)',
      'Cantidad de servicio.',
      'Puede mostrar unidad del servicio como hint.',
      { required: true },
    ),
    grillaCampo(
      'P. unit.',
      'Moneda (UnitPriceTextInput)',
      'Precio neto del servicio.',
      'Lista de precios del POS.',
      { required: true },
    ),
    grillaCampo(
      'IGV',
      'Select (Sí / No)',
      'Afectación IGV.',
      'Igual que producto.',
    ),
    grillaCampo(
      'Subtotal',
      'Solo lectura',
      'Total línea.',
      'Calculado.',
      { editable: false, automatic: true },
    ),
    grillaCampo(
      'Quitar ítem (✕)',
      'Botón',
      'Elimina la fila.',
      '',
    ),
  ],
  relatedFunctions: ['general-grilla-detalle-ventas-producto', 'ventas-osv-form'],
  roles: ROLES.ventas,
});

export const grillaDetalleGuiaIngresoDoc = detalleBase({
  id: 'general-grilla-detalle-guia-ingreso',
  name: 'Grilla — fila Guía de ingreso (Almacenes)',
  route: '/warehouses/1/entry-guides/create',
  locationPath: 'Almacenes > Documentos > Guía de ingreso > líneas de producto',
  generalDescription:
    'Movimiento que aumenta stock. Incluye evento de kardex (p. ej. Compra), costo unitario valorizado e IGV en guías de ingreso (isEntryGuide).',
  extraPrerequisites: ['Almacén de trabajo seleccionado.'],
  interfaceElements: [
    grillaCampo(
      'Producto',
      'Buscador (ProductSelector)',
      'Producto que ingresa.',
      'Obligatorio.',
      { required: true },
    ),
    grillaCampo(
      'Kardex',
      'Select',
      'Evento de kardex de la línea.',
      'Ingreso: Compra, Entrada de producción, Entrada producto terminado, Devolución, Transferencia gratuita, Descuento obtenido, Descuento con mercadería. En saldo inicial solo «Saldo Inicial» y bloqueado.',
      { required: true },
    ),
    grillaCampo(
      'Cant.',
      'Texto decimal',
      'Cantidad que ingresa.',
      'Patrón de decimales validado al tipear.',
      { required: true },
    ),
    grillaCampo(
      'Unid.',
      'Select (UnitSelect)',
      'Unidad; emptyLabel «—».',
      'showCodeOnly en captura rápida.',
    ),
    grillaCampo(
      'Proy.',
      'Select',
      'Proyecto.',
      'Si showProjectField activo.',
    ),
    grillaCampo(
      'C.Unit.',
      'Moneda (UnitPriceTextInput)',
      'Costo unitario de valorización.',
      'Solo en guía de ingreso valorizada; toggle precios con IGV arriba de la grilla.',
      { required: true },
    ),
    grillaCampo(
      'IGV',
      'Select (Sí / No)',
      'Línea gravada.',
      'Con productsIgvEnabled en guías de ingreso.',
    ),
    grillaCampo(
      'Total',
      'Solo lectura',
      'Importe de línea en filas agregadas.',
      'Puede variar según tipo de guía en listado inferior.',
      { editable: false, automatic: true },
    ),
    grillaCampo(
      'Quitar',
      'Botón',
      'Elimina línea.',
      '',
    ),
  ],
  relatedFunctions: [
    'general-grilla-detalle-controles-comunes',
    'almacenes-grilla-productos-items',
    'general-grilla-detalle-guia-salida-traslado',
  ],
  roles: ROLES.almacenes,
});

export const grillaDetalleGuiaSalidaTrasladoDoc = detalleBase({
  id: 'general-grilla-detalle-guia-salida-traslado',
  name: 'Grilla — fila Guía salida / traslado / recepción (Almacenes)',
  route: '/warehouses/1/exit-guides/create',
  locationPath:
    'Almacenes > Guía de salida, traslado o recepción > líneas de producto',
  generalDescription:
    'Salidas y traslados: sin columna Kardex en traslado/recepción en captura rápida; costo unitario de referencia (solo lectura o editable según useReferenceLastPurchaseCost); condición de mercadería en salida/traslado.',
  interfaceElements: [
    grillaCampo(
      'Producto',
      'Buscador (ProductSelector)',
      'Producto que sale o se transfiere.',
      'En salida puede mostrar stock disponible en el selector.',
      { required: true },
    ),
    grillaCampo(
      'Kardex',
      'Select',
      'Evento de salida.',
      'Solo en guía de salida (no traslado/recepción en barra rápida): Venta, Salida a producción, Descuento concedido, Consumo, Transferencia gratuita, Descuento con mercadería.',
      { required: true },
    ),
    grillaCampo(
      'Cant.',
      'Texto decimal',
      'Cantidad del movimiento.',
      'Validación de stock en salida.',
      { required: true },
    ),
    grillaCampo(
      'Unid.',
      'Select (UnitSelect)',
      'Unidad de medida.',
      '',
    ),
    grillaCampo(
      'Proy.',
      'Select',
      'Proyecto.',
      'Si aplica.',
    ),
    grillaCampo(
      'C.Unit. / C.Unit. ref.',
      'Moneda (input)',
      'Costo de salida o costo referencial.',
      'Solo lectura si toma último costo de compra; editable si isReferenceCostEditable. En salida estricta puede ser obligatorio según configuración.',
    ),
    grillaCampo(
      'Cond.',
      'Select',
      'Estado físico del ítem.',
      'Solo salida y traslado: Bueno, Dañado, Defectuoso, Reutilizable, No reutilizable.',
      { required: true },
    ),
    grillaCampo(
      'IGV',
      'Select',
      'En ingresos valorizados vinculados a documento.',
      'No siempre visible en salida pura.',
    ),
    grillaCampo(
      'Importar Excel',
      'Botón + archivo',
      'Plantilla de ítems de guía.',
      'GuideItemsImportPreviewModal.',
    ),
  ],
  validations: [
    'Stock suficiente en salidas.',
    'Cond. obligatoria en salida/traslado.',
    'Producto y cantidad > 0.',
  ],
  relatedFunctions: [
    'general-grilla-detalle-guia-ingreso',
    'almacenes-grilla-productos-items',
  ],
  roles: ROLES.almacenes,
});

export const grillaProductosDetalleSections = [
  grillaControlesComunesDoc,
  grillaDetalleRequerimientoDoc,
  grillaDetalleOcComprasDoc,
  grillaDetalleOsComprasDoc,
  grillaDetalleVentasProductoDoc,
  grillaDetalleVentasServicioDoc,
  grillaDetalleGuiaIngresoDoc,
  grillaDetalleGuiaSalidaTrasladoDoc,
];
